/**
 * 외부 내비게이션 앱 딥링크 유틸.
 * - TMap: 물류 최적화(TMAP 경유지 100 최적화 API) 지원 → 주력
 * - 카카오내비: 단일 목적지 딥링크
 * - Google Maps: 웹 fallback
 *
 * 기획 판단:
 *   1) 클라이언트에서 자체 TSP를 돌려 stop 순서를 결정
 *   2) 각 stop 클릭 시 TMap 딥링크로 턴바이턴 안내
 *   3) TMap waypoint 최적화 API는 서버측 Cloud Function에서 보조적으로 호출
 */

export interface Coord {
  lat: number;
  lng: number;
  name?: string;
}

/** TMap 단일 목적지 경로안내: tmap://route?goalname=..&goalx=..&goaly=.. */
export function tmapRouteUrl(goal: Coord): string {
  const q = new URLSearchParams();
  if (goal.name) q.set("goalname", goal.name);
  q.set("goalx", String(goal.lng));
  q.set("goaly", String(goal.lat));
  return `tmap://route?${q.toString()}`;
}

/** 카카오내비: kakaonavi://navigate?name=..&x=..&y=..&coord_type=wgs84 */
export function kakaoNaviUrl(goal: Coord): string {
  const q = new URLSearchParams();
  if (goal.name) q.set("name", goal.name);
  q.set("x", String(goal.lng));
  q.set("y", String(goal.lat));
  q.set("coord_type", "wgs84");
  return `kakaonavi://navigate?${q.toString()}`;
}

/** 구글 맵스 웹 fallback */
export function googleMapsDirectionsUrl(goal: Coord, origin?: Coord): string {
  const q = new URLSearchParams();
  q.set("api", "1");
  q.set("destination", `${goal.lat},${goal.lng}`);
  if (origin) q.set("origin", `${origin.lat},${origin.lng}`);
  q.set("travelmode", "driving");
  return `https://www.google.com/maps/dir/?${q.toString()}`;
}

/**
 * 플랫폼 자동 선택.
 * 모바일에선 설치된 앱 여부를 알기 어렵기에, 우선순위(tmap > kakao)로 딥링크를 시도.
 * 브라우저는 이어서 fallback 처리.
 */
export function openNavigation(
  goal: Coord,
  preferred: "tmap" | "kakao" = "tmap",
  origin?: Coord,
) {
  const primary = preferred === "tmap" ? tmapRouteUrl(goal) : kakaoNaviUrl(goal);
  const fallback = googleMapsDirectionsUrl(goal, origin);

  // 앱으로 전환되면(설치됨) fallback 을 취소하기 위한 감지
  let switched = false;
  const mark = () => {
    switched = true;
  };
  document.addEventListener("visibilitychange", mark);
  window.addEventListener("blur", mark);

  // 현재 탭에서 앱 딥링크 시도 (새 탭을 열면 미설치 시 빈 탭만 남음)
  window.location.href = primary;

  // 1.5초 내 앱 전환이 없으면(미설치 등) 웹 지도로 fallback
  window.setTimeout(() => {
    document.removeEventListener("visibilitychange", mark);
    window.removeEventListener("blur", mark);
    if (!switched && document.visibilityState === "visible") {
      // 같은 탭을 구글 지도로 바꾸면 앱 화면(배송 상세)이 통째로 사라진다 — 새 탭으로 열고 앱은 그대로 둔다(3차 점검 10-#2)
      window.open(fallback, "_blank", "noopener");
    }
  }, 1500);
}

/**
 * 출발지 — 부산 사상구 하신번영로 440 (고정).
 */
export const ORIGIN_ADDRESS = "부산광역시 사상구 하신번영로 440";
export const ORIGIN_DEFAULT: Coord = {
  lat: 35.1530, // 대략적 fallback (실제 운영시 V-World 로 한번 조회해 고정)
  lng: 128.9799,
  name: "물류 출발지",
};
